import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import type { ReactNode } from "react";
import { Listbox } from "../../../../components/Listbox";
import { Text } from "../../../../components/Text";
import type { RichTextEditorMentionOption } from "../types";
import styles from "./MentionList.module.scss";

export type MentionListRef = {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
};

type MentionListProps = {
  items: RichTextEditorMentionOption[];
  command: (item: RichTextEditorMentionOption) => void;
  renderOption?: (option: RichTextEditorMentionOption) => ReactNode;
};

export const MentionList = forwardRef<MentionListRef, MentionListProps>(
  ({ items, command, renderOption }, ref) => {
    const [highlightedIndex, setHighlightedIndex] = useState(0);

    useEffect(() => setHighlightedIndex(0), [items]);

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }: { event: KeyboardEvent }) => {
        if (event.key === "ArrowUp") {
          if (!items.length) return false;
          setHighlightedIndex((i) => (i + items.length - 1) % items.length);
          return true;
        }
        if (event.key === "ArrowDown") {
          if (!items.length) return false;
          setHighlightedIndex((i) => (i + 1) % items.length);
          return true;
        }
        if (event.key === "Enter") {
          if (!items.length) return false;
          const item = items[highlightedIndex];
          if (!item) return false;
          command(item);
          return true;
        }
        return false;
      },
    }));

    const handleSelectItem = (item: RichTextEditorMentionOption) => {
      if (item) {
        command(item);
      }
      setHighlightedIndex(0);
    };

    if (!items.length) {
      return (
        <div className={styles["empty"]}>
          <Text subdued size="small">
            No results found
          </Text>
        </div>
      );
    }

    return (
      <Listbox<RichTextEditorMentionOption>
        aria-label="Mention suggestions"
        items={items}
        selectionMode="single"
      >
        {({ items: listItems }) =>
          listItems.map((item, index) => (
            <Listbox.Option
              onClick={() => handleSelectItem(item)}
              key={String(item.id)}
              item={item}
              data-interactive={
                highlightedIndex === index ? "hover" : undefined
              }
            >
              {renderOption ? renderOption(item) : item.label}
            </Listbox.Option>
          ))
        }
      </Listbox>
    );
  },
);

MentionList.displayName = "MentionList";
